/*
 * Running total on the add to cart button.
 *
 * The design's button reads "add to cart · RM65.00" — quantity times the
 * selected variant, plus any ticked add-ons. Dawn's button is a plain label,
 * so this appends the figure and keeps it current.
 *
 * Dawn replaces the buy-buttons markup wholesale on a variant change, which
 * wipes anything written into it. Rather than racing that, this re-applies
 * after the variant event and also watches the container for replacement.
 *
 * Purely additive: if anything here fails, the button keeps Dawn's own label
 * and still adds to cart.
 */
if (!customElements.get('nq-cart-total')) {
  customElements.define(
    'nq-cart-total',
    class NqCartTotal extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.separator = this.dataset.separator || ' · ';
        this.prices = this.readPrices();
        this.tiers = this.readTiers();

        this.section = this.closest('section') || document;
        this.form = this.section.querySelector('form[action*="/cart/add"]');
        if (!this.form) return;

        this.onInput = this.scheduleUpdate.bind(this);
        this.section.addEventListener('change', this.onInput);
        this.section.addEventListener('input', this.onInput);

        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.sectionId) return;
          this.scheduleUpdate();
        });

        // The buy-buttons block is re-rendered on variant change; re-apply
        // once the new button is in place.
        const host = this.section.querySelector('.product-form__buttons') || this.section;
        this.observer = new MutationObserver(() => this.scheduleUpdate());
        this.observer.observe(host, { childList: true, subtree: true });

        this.update();
      }

      disconnectedCallback() {
        this.section?.removeEventListener('change', this.onInput);
        this.section?.removeEventListener('input', this.onInput);
        this.observer?.disconnect();
        this.unsubscribeVariant?.();
      }

      readPrices() {
        const el = this.querySelector('[data-nq-prices]');
        if (!el) return {};
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          console.error('nq-cart-total: could not parse variant prices', e);
          return {};
        }
      }

      readTiers() {
        const el = this.querySelector('[data-nq-tiers]');
        if (!el) return [];
        try {
          // Highest threshold first, so the first match is the best tier.
          return JSON.parse(el.textContent)
            .filter((t) => Number.isFinite(t.min) && Number.isFinite(t.price))
            .sort((a, b) => b.min - a.min);
        } catch (e) {
          console.error('nq-cart-total: could not parse tiers', e);
          return [];
        }
      }

      /* Mix & Match prices the set, not the add-ons, so only the quantity of
         this product counts toward a tier. Falls back to the variant price
         when no tier is reached, or when this product does not qualify. */
      unitPriceFor(quantity, variantPrice) {
        const tier = this.tiers.find((t) => quantity >= t.min);
        return tier ? tier.price : variantPrice;
      }

      scheduleUpdate() {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.update(), 60);
      }

      get submitLabel() {
        const button = this.section.querySelector('.product-form__submit');
        return button ? button.querySelector('span') : null;
      }

      unitPrice() {
        const idInput = this.form.querySelector('[name="id"]');
        const id = idInput ? idInput.value : null;
        if (id && this.prices[id] != null) return this.prices[id];
        const first = Object.values(this.prices)[0];
        return first == null ? null : first;
      }

      quantity() {
        // Dawn puts the quantity input outside the <form>, associated by its
        // form attribute, so it has to be found on the section.
        const q = this.section.querySelector('[name="quantity"]');
        const n = q ? parseInt(q.value, 10) : 1;
        return Number.isFinite(n) && n > 0 ? n : 1;
      }

      addonsTotal() {
        let sum = 0;
        this.section.querySelectorAll('[data-nq-addon]:checked').forEach((el) => {
          const p = parseInt(el.dataset.nqAddonPrice, 10);
          if (Number.isFinite(p)) sum += p;
        });
        return sum;
      }

      formatMoney(cents) {
        return window.nqMoney(cents, this.dataset.moneyFormat);
      }

      update() {
        const label = this.submitLabel;
        if (!label) return;

        const button = label.closest('.product-form__submit');
        // Sold out and unavailable states say something other than "add to
        // cart"; a price on those would read as nonsense.
        if (button?.hasAttribute('disabled')) return;

        const variantPrice = this.unitPrice();
        if (variantPrice == null) return;

        const quantity = this.quantity();
        const unit = this.unitPriceFor(quantity, variantPrice);
        const total = unit * quantity + this.addonsTotal();

        // Always rebuild from the base label so repeated runs cannot stack.
        if (!this.baseLabel || !label.textContent.startsWith(this.baseLabel)) {
          this.baseLabel = label.textContent.split(this.separator)[0].trim();
        }
        label.textContent = `${this.baseLabel}${this.separator}${this.formatMoney(total)}`;
      }
    }
  );
}
